/**
 * Ce que montre le centre du live du masteur (pur) : l'élève regardé, l'activité qui est la sienne, les
 * onglets dans leur ordre, l'onglet ouvert, et ce que le contenu rend. Lu par `<TutorLiveCenter>` ; testé
 * à côté.
 */
import type { UICorrectionFeedback } from "../../../ports/primary/ui/types/studentActivity"
import type {
    UITutorLiveCenterTab,
    UITutorLiveFocus,
    UITutorLiveStudent,
} from "../../../ports/primary/ui/types/tutorLive"
import type { TutorAnnotationVisual } from "../../molecules/TutorAnnotationToolbar/TutorAnnotationToolbar"

/**
 * Ce que rend le contenu du centre : la liste (téléphone), les contrôles, la visio de l'élève, la vue de
 * l'activité, ou rien — une présentation sans activité à montrer ne montre jamais un élève.
 */
export type TutorLiveCenterBody = "activities" | "assessments" | "visio" | "activity" | "nothing"

export type TutorLiveCenterView = {
    /**
     * L'élève regardé : celui qu'on a choisi, sinon celui de l'activité ouverte. Pendant une présentation,
     * celui de l'activité présentée, quelle que soit la sélection.
     */
    student?: UITutorLiveStudent
    /** L'activité ouverte, si elle est à lui ; sinon rien (l'écran la change avec la sélection). */
    ownFocus: UITutorLiveFocus | null
    /** Les onglets, dans leur ordre. */
    keys: UITutorLiveCenterTab[]
    /** L'onglet ouvert : celui demandé s'il existe, sinon le rendu de son activité, sinon la visio. */
    current: UITutorLiveCenterTab
    /** La vue de l'activité que montre le centre ; `null` quand il montre la liste, la visio ou les contrôles. */
    activityTab: Exclude<UITutorLiveCenterTab, "visio" | "activities" | "assessments"> | null
    /** Ce que rend le contenu (la région que « Présenter » partage). */
    body: TutorLiveCenterBody
}

/**
 * Les élèves voient le centre (pur) : une présentation tourne, **ou elle part** (`pending` : le sélecteur
 * ouvert, la piste en route), ou l'onglet entier part déjà (`centerOnly`). Le départ compte : la piste
 * recadrée est publiée AVANT que la présentation ne soit marquée (`setFocusScreenShare(true)` suit
 * `publishTrack`), et ses premières images partaient avec la correction du champion encore posée. L'ancien
 * `ScreenShareToggleButton.web.js` avait le même ordre (la correction de `TodoModal` ne disparaissait
 * qu'avec `isFocusScreenShare`, après la publication) : le centre la cache dès le départ. Un départ
 * annulé (le sélecteur refermé) la rend aussitôt.
 */
export function tutorLiveCenterWatched(input: {
    present?: { on: boolean; pending?: boolean }
    centerOnly: boolean
}): boolean {
    const { present, centerOnly } = input
    return present?.on === true || present?.pending === true || centerOnly
}

/** Ce qu'une présentation peut montrer au groupe. */
const SHOWABLE: UITutorLiveCenterTab[] = ["statement", "work", "whiteboard"]

export function tutorLiveCenterView(input: {
    students: UITutorLiveStudent[]
    selectedId?: string
    focus?: UITutorLiveFocus | null
    tab: UITutorLiveCenterTab
    phone: boolean
    /**
     * Les élèves voient le centre (une présentation tourne, ou l'onglet entier part déjà) : seul ce qui se
     * montre au groupe reste (l'énoncé, le rendu, le tableau), et jamais la visio, la liste ni les
     * contrôles d'un élève — même sans activité à montrer.
     */
    presenting?: boolean
}): TutorLiveCenterView {
    const { students, selectedId, focus, tab, phone, presenting = false } = input
    const byId = (id?: string) => (id != null ? students.find((one) => one.id === id) : undefined)
    // L'élève regardé fait foi : la tête, la liste du téléphone et leurs gestes parlent de lui. Pendant une
    // présentation, l'activité présentée fait foi : toucher un autre élève ne la remplace pas par sa visio
    // (l'ancien `TodoModal` recouvrait la salle, on ne pouvait pas toucher un autre élève).
    const student = (presenting ? byId(focus?.studentId) : undefined) ?? byId(selectedId) ?? byId(focus?.studentId)
    const ownFocus = focus != null && focus.studentId === student?.id ? focus : null
    const keys: UITutorLiveCenterTab[] = phone ? ["activities", "visio"] : ["visio"]
    if (ownFocus != null) {
        const views: UITutorLiveCenterTab[] = ["statement", "work"]
        if (ownFocus.quiz !== undefined) views.unshift("quiz")
        if (ownFocus.whiteboard === true) views.unshift("whiteboard")
        // Au téléphone, l'ordre s'inverse : le rendu passe devant, c'est lui qui arrive (Wajih, 02/11).
        keys.push(...(phone ? views.reverse() : views))
    }
    // Les contrôles : un onglet tant qu'on les regarde, chez un élève qui en a (pas un stage).
    if (tab === "assessments" && student != null && student.nextAssessment !== undefined) keys.push("assessments")
    // Pendant une présentation, les élèves voient le centre : ni la visio d'un élève, ni ses contrôles, ni
    // son quiz, ni sa liste — avec ou sans activité à montrer.
    if (presenting) {
        const showable = keys.filter((key) => SHOWABLE.includes(key))
        keys.splice(0, keys.length, ...showable)
    }
    const current: UITutorLiveCenterTab = keys.includes(tab) ? tab : ownFocus != null ? "work" : "visio"
    const activityTab =
        ownFocus != null && current !== "visio" && current !== "activities" && current !== "assessments"
            ? current
            : null
    return {
        student,
        ownFocus,
        keys,
        current,
        activityTab,
        body: bodyOf({ presenting, student, current, activityTab }),
    }
}

function bodyOf(args: {
    presenting: boolean
    student?: UITutorLiveStudent
    current: UITutorLiveCenterTab
    activityTab: TutorLiveCenterView["activityTab"]
}): TutorLiveCenterBody {
    const { presenting, student, current, activityTab } = args
    if (activityTab != null) return "activity"
    // Le filet : présenter n'offre que l'activité ; sans elle, la région reste vide plutôt que de partager
    // la vidéo et le prénom d'un élève.
    if (presenting || student == null) return "nothing"
    if (current === "activities") return "activities"
    if (current === "assessments") return "assessments"
    return "visio"
}

/**
 * Le visuel du tableau blanc dans la barre de dessin, pendant une présentation (pur). Le tableau part chez
 * les élèves : sa bascule « Visuel » et l'avis du masteur n'y sont plus posés (`<TutorLiveActivityView
 * presenting>`), ils passent dans la barre — l'ancien `WhiteboardTab` les gardait pendant la présentation
 * (`FloatingControls` : l'œil ; `FeedbackRow` : les pouces, tant que l'image est montrée). Seulement sur le
 * tableau blanc, un visuel prêt, et la bascule offerte ; hors présentation, la vue les porte elle-même.
 */
export function presentationVisual(input: {
    presenting: boolean
    view: "statement" | "work" | "split" | "quiz" | "whiteboard" | null
    visual: UITutorLiveFocus["visual"]
    onToggle?: () => void
    onFeedback?: (feedback: UICorrectionFeedback) => void
}): TutorAnnotationVisual | undefined {
    const { presenting, view, visual, onToggle, onFeedback } = input
    if (!presenting || view !== "whiteboard" || visual?.state !== "ready" || onToggle == null) return undefined
    return {
        shown: visual.shown,
        onToggle,
        feedback:
            visual.shown && visual.feedback !== undefined && onFeedback != null
                ? { value: visual.feedback, onChange: onFeedback }
                : undefined,
    }
}
